import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import type { AppTheme } from "./theme.interface";

const THEME_STORAGE_KEY = "theme";
const DEFAULT_THEME: AppTheme = "light";

function readStoredTheme(): AppTheme {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "dark" || stored === "light" ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function applyThemeToDom(theme: AppTheme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function disableTransitionsTemporarily(): () => void {
  const style = document.createElement("style");
  style.setAttribute("data-disable-theme-transition", "");
  style.textContent =
    "*,*::before,*::after{-webkit-transition:none!important;transition:none!important}";
  document.head.appendChild(style);

  return () => {
    void document.documentElement.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        style.remove();
      });
    });
  };
}

function runWithThemeViewTransition(updateDom: () => void): void {
  if (prefersReducedMotion()) {
    const restore = disableTransitionsTemporarily();
    updateDom();
    restore();
    return;
  }

  const doc = document as Document & {
    startViewTransition?: (callback: () => void) => unknown;
  };

  if (typeof doc.startViewTransition === "function") {
    doc.startViewTransition(updateDom);
    return;
  }

  const restore = disableTransitionsTemporarily();
  updateDom();
  restore();
}

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private readonly themeSubject = new BehaviorSubject<AppTheme>(
    readStoredTheme(),
  );

  readonly theme$: Observable<AppTheme> = this.themeSubject.asObservable();

  constructor() {
    applyThemeToDom(this.themeSubject.value);
  }

  get theme(): AppTheme {
    return this.themeSubject.value;
  }

  get isDark(): boolean {
    return this.theme === "dark";
  }

  setTheme(theme: AppTheme): void {
    if (theme === this.theme) {
      return;
    }

    runWithThemeViewTransition(() => {
      applyThemeToDom(theme);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch {}
      this.themeSubject.next(theme);
    });
  }

  toggleTheme(): void {
    this.setTheme(this.isDark ? "light" : "dark");
  }
}
